import { api } from './api';
import { Club, SubscriptionTier } from '../types';

interface ValidateInvitationResponse {
  valid: boolean;
  invitation?: {
    clubName: string;
    invitedEmail: string;
    invitedFirstName: string;
    invitedLastName: string;
    invitedRole: string;
    expiresAt: string;
  };
  error?: {
    code: string;
    message: string;
  };
}

interface AcceptInvitationRequest {
  token: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

interface AcceptInvitationResponse {
  user: {
    id: string;
    email: string;
    role: string;
    profile: {
      firstName: string;
      lastName: string;
      clubId?: string;
    };
  };
  token: string;
}

interface CreateClubRequest {
  name: string;
  affiliatedFA: string;
  address: string;
}

interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export const clubService = {
  /**
   * Create a new club
   */
  async createClub(data: CreateClubRequest): Promise<Club> {
    const response = await api.post<{ data: Club }>('/clubs', data);
    return response.data.data;
  },

  /**
   * Create Stripe Checkout session for subscription
   */
  async createCheckoutSession(
    clubId: string,
    tier: SubscriptionTier,
    discountCode?: string
  ): Promise<CheckoutSessionResponse> {
    const response = await api.post<{ data: CheckoutSessionResponse }>(
      `/clubs/${clubId}/checkout-session`,
      { tier, discountCode }
    );
    return response.data.data;
  },

  /**
   * Validate an invitation token
   */
  async validateInvitation(token: string): Promise<ValidateInvitationResponse> {
    try {
      const response = await api.get<{ data: ValidateInvitationResponse }>(
        `/clubs/invitations/validate?token=${encodeURIComponent(token)}`
      );
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.error) {
        return {
          valid: false,
          error: error.response.data.error,
        };
      }
      throw error;
    }
  },

  /**
   * Accept an invitation and create/link account
   */
  async acceptInvitation(data: AcceptInvitationRequest): Promise<AcceptInvitationResponse> {
    const response = await api.post<{ data: AcceptInvitationResponse }>(
      '/clubs/invitations/accept',
      data
    );
    return response.data.data;
  },

  /**
   * Get club dashboard statistics
   */
  async getDashboardStats(clubId: string): Promise<import('@player-assessment/shared-types').ClubDashboardStats> {
    const response = await api.get<{ data: { greeting: string; stats: import('@player-assessment/shared-types').ClubDashboardStats } }>(
      `/clubs/${clubId}/dashboard`
    );
    return response.data.data.stats;
  },

  /**
   * Get club details
   */
  async getClub(clubId: string): Promise<Club> {
    const response = await api.get<{ data: Club }>(`/clubs/${clubId}`);
    return response.data.data;
  },

  /**
   * Get all teams for a club
   */
  async getClubTeams(clubId: string): Promise<any[]> {
    const response = await api.get<{ data: any[] }>(`/clubs/${clubId}/teams`);
    return response.data.data;
  },

  /**
   * Create a team for a club
   */
  async createClubTeam(clubId: string, teamData: { name: string; ageGroup: string; season: string }): Promise<any> {
    const response = await api.post<{ data: any }>(`/clubs/${clubId}/teams`, teamData);
    return response.data.data;
  },

  /**
   * Delete a team from a club (soft delete)
   */
  async deleteClubTeam(clubId: string, teamId: string): Promise<void> {
    await api.delete(`/clubs/${clubId}/teams/${teamId}`);
  },

  /**
   * Assign or reassign a coach to a team
   */
  async assignCoachToTeam(clubId: string, teamId: string, coachId: string): Promise<void> {
    await api.put(`/clubs/${clubId}/teams/${teamId}/coach`, { coachId });
  },

  /**
   * Get subscription details for a club
   */
  async getSubscription(clubId: string): Promise<import('@player-assessment/shared-types').ClubSubscription | null> {
    try {
      const response = await api.get<{ data: import('@player-assessment/shared-types').ClubSubscription }>(
        `/clubs/${clubId}/subscription`
      );
      return response.data.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Get all contacts for a club
   */
  async getContacts(clubId: string, role?: string, search?: string): Promise<import('@player-assessment/shared-types').ClubContact[]> {
    const params = new URLSearchParams();
    if (role) params.append('role', role);
    if (search) params.append('search', search);
    
    const queryString = params.toString();
    const url = `/clubs/${clubId}/contacts${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get<{ data: import('@player-assessment/shared-types').ClubContact[] }>(url);
    return response.data.data;
  },

  /**
   * Remove a member from the club
   */
  async removeMember(clubId: string, userId: string): Promise<void> {
    await api.delete(`/clubs/${clubId}/members/${userId}`);
  },

  /**
   * Create an invitation
   */
  async createInvitation(clubId: string, data: import('@player-assessment/shared-types').CreateClubInvitationRequest): Promise<import('@player-assessment/shared-types').ClubInvitation> {
    const response = await api.post<{ data: import('@player-assessment/shared-types').ClubInvitation }>(
      `/clubs/${clubId}/invitations`,
      data
    );
    return response.data.data;
  },

  /**
   * Get all invitations for a club
   */
  async getInvitations(clubId: string): Promise<import('@player-assessment/shared-types').ClubInvitation[]> {
    const response = await api.get<{ data: import('@player-assessment/shared-types').ClubInvitation[] }>(
      `/clubs/${clubId}/invitations`
    );
    return response.data.data;
  },

  /**
   * Revoke an invitation
   */
  async revokeInvitation(clubId: string, invitationId: string): Promise<void> {
    await api.delete(`/clubs/${clubId}/invitations/${invitationId}`);
  },

  /**
   * Update club details
   */
  async updateClub(clubId: string, data: import('@player-assessment/shared-types').UpdateClubRequest): Promise<Club> {
    const response = await api.put<{ data: Club }>(`/clubs/${clubId}`, data);
    return response.data.data;
  },
};
